import React, { useState } from "react";
import { Typography, Menu, MenuItem, Divider } from "@mui/material";
import AlignHorizontalCenterOutlinedIcon from "@mui/icons-material/AlignHorizontalCenterOutlined";
import Rotate90DegreesCwOutlinedIcon from "@mui/icons-material/Rotate90DegreesCwOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import ZoomOutMapOutlinedIcon from "@mui/icons-material/ZoomOutMapOutlined";
import TransformOutlinedIcon from "@mui/icons-material/TransformOutlined";
import HeightOutlinedIcon from "@mui/icons-material/HeightOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useData } from "../hooks/useData";
import SkewDialog from "./SkewDialog";
import MoveDraggable from "./MoveDraggable";
import SizeDialog from "./SizeDialog";
import ScaleDialog from "./ScaleDialog";
import RadiusDialog from "./RadiusDialog";
import AngleDialog from "./AngleDialog";
import StrokeDialog from "./StrokeDialog";
import AlignDialog from "./AlignDialog"; // Add this import

const ToolsMenu = ({ canvas }) => {
  const { selectedObject } = useData();
  const [anchorEl, setAnchorEl] = useState(null);
  const [skewDialogOpen, setSkewDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [sizeDialogOpen, setSizeDialogOpen] = useState(false);
  const [scaleDialogOpen, setScaleDialogOpen] = useState(false);
  const [radiusDialogOpen, setRadiusDialogOpen] = useState(false);
  const [angleDialogOpen, setAngleDialogOpen] = useState(false);
  const [strokeDialogOpen, setStrokeDialogOpen] = useState(false);
  const [alignDialogOpen, setAlignDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  // No objects or no canvas
  // const isCanvasEmpty = !canvas || canvas.getObjects().length === 0;
  // Single object selected (not multiple)
  const isSingleObject =
    selectedObject && selectedObject.get("type") !== "activeselection";

  // Multiple objects selected
  // Selected object is an ActiveSelection
  const isActiveSelection =
    selectedObject && selectedObject.get("type") === "activeselection";

  // Selected object is a circle
  const isCircle = selectedObject && selectedObject.type === "circle";

  const handleClick = (event) => {
    console.log("[ToolsMenu]: handleClick");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    console.log("[ToolsMenu]: handleClose");
    setAnchorEl(null);
  };

  const handleAlign = () => {
    setAlignDialogOpen(true);
    setAnchorEl(null);
  };

  const handleAngle = () => {
    console.log("[ToolsMenu]: handleAngle");
    setAngleDialogOpen(true);
    setAnchorEl(null);
  };

  const handleMove = () => {
    console.log("[ToolsMenu]: handleMove");
    if (!moveDialogOpen) {
      setMoveDialogOpen(true);
    }
    setAnchorEl(null);
  };

  const handleRadius = () => {
    console.log("[ToolsMenu]: handleRadius");
    setRadiusDialogOpen(true);
    setAnchorEl(null);
  };

  const handleScale = () => {
    console.log("[ToolsMenu]: handleScale");
    setScaleDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSkew = () => {
    setSkewDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSize = () => {
    setSizeDialogOpen(true);
    setAnchorEl(null);
  };

  const handleStroke = () => {
    setStrokeDialogOpen(true);
    setAnchorEl(null);
  };

  const handleCloseMenu = () => {
    console.log("[ToolsMenu]: handleCloseMenu");
    setAnchorEl(null);
  };

  const dialogs = [
    {
      name: "Align",
      icon: AlignHorizontalCenterOutlinedIcon,
      disabled: !isActiveSelection,
      handler: handleAlign,
    },
    {
      name: "Angle",
      icon: Rotate90DegreesCwOutlinedIcon,
      disabled: !isSingleObject || isCircle,
      handler: handleAngle,
    },
    { divider: true },
    {
      name: "Move",
      icon: EastOutlinedIcon,
      disabled: !isSingleObject,
      handler: handleMove,
    },
    {
      name: "Radius",
      icon: ArchitectureOutlinedIcon,
      disabled: !isCircle,
      handler: handleRadius,
    },
    {
      name: "Scale",
      icon: ZoomOutMapOutlinedIcon,
      disabled: !isSingleObject || isCircle,
      handler: handleScale,
    },
    {
      name: "Skew",
      icon: TransformOutlinedIcon,
      disabled: !isSingleObject,
      handler: handleSkew,
    },
    {
      name: "Size",
      icon: HeightOutlinedIcon,
      disabled: !isSingleObject,
      handler: handleSize,
    },
    {
      name: "Stroke",
      icon: BorderColorOutlinedIcon,
      disabled: !isSingleObject,
      handler: handleStroke,
    },
    {
      name: "Close",
      icon: CloseOutlinedIcon,
      disabled: false,
      handler: handleCloseMenu,
    },
  ];

  return (
    <>
      <Typography
        variant="body1"
        onClick={handleClick}
        sx={{
          fontFamily: "Roboto",
          fontWeight: 500,
          color: "#000000",
          cursor: "pointer",
          "&:hover": {
            textDecoration: "underline",
            color: "#000000",
          },
        }}
      >
        Tools
      </Typography>
      <Menu
        id="tools-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        MenuListProps={{
          "aria-labelledby": "tools-button",
        }}
        sx={{
          "& .MuiPaper-root": {
            minWidth: 200,
            maxWidth: 240,
            boxShadow: 3,
          },
        }}
      >
        {dialogs.map(
          ({ name, icon: Icon, disabled, handler, divider }, index) =>
            divider ? (
              <Divider key={`divider-${index}`} />
            ) : (
              <MenuItem key={name} disabled={disabled} onClick={handler}>
                <Icon sx={{ mr: 1, fontSize: 20 }} />
                {name}
              </MenuItem>
            )
        )}
      </Menu>
      <SkewDialog
        open={skewDialogOpen}
        onClose={() => setSkewDialogOpen(false)}
        canvas={canvas}
        selectedObject={selectedObject}
      />
      <MoveDraggable
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        canvas={canvas}
        selectedObject={selectedObject}
      />
      <SizeDialog
        open={sizeDialogOpen}
        onClose={() => setSizeDialogOpen(false)}
        canvas={canvas}
        selectedObject={selectedObject}
      />
      <ScaleDialog
        open={scaleDialogOpen}
        onClose={() => setScaleDialogOpen(false)}
        canvas={canvas}
        selectedObject={selectedObject}
      />
      <RadiusDialog
        open={radiusDialogOpen}
        onClose={() => setRadiusDialogOpen(false)}
        canvas={canvas}
        selectedObject={selectedObject}
      />
      <AngleDialog
        open={angleDialogOpen}
        onClose={() => setAngleDialogOpen(false)}
        canvas={canvas}
        selectedObject={selectedObject}
      />
      <StrokeDialog
        open={strokeDialogOpen}
        onClose={() => setStrokeDialogOpen(false)}
        canvas={canvas}
        selectedObject={selectedObject}
      />
      <AlignDialog
        open={alignDialogOpen}
        onClose={() => setAlignDialogOpen(false)}
        canvas={canvas}
        selectedObject={selectedObject}
      />
    </>
  );
};

export default ToolsMenu;
